<template>
  <div class="my-6">
    <!-- Header -->
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-[#34495e]">Payment Checkout</h2>
      <p class="mt-2 text-zinc-500">
        Review your payment details before proceeding.
      </p>
    </div>

    <!-- Payment Summary -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <!-- Summary -->
      <div
        class="lg:col-span-2 rounded-2xl border border-zinc-200 bg-white shadow-sm"
      >
        <div class="border-b border-zinc-200 p-6 space-y-1">
          <h3 class="text-lg font-semibold text-secondary">Payment Summary</h3>
          <h2 class="text-sm text-zinc-500">
            Reference Number: {{ $store.state.planDetails.referenceNumber }}
          </h2>
        </div>

        <div class="space-y-6 p-6">
          <div class="flex justify-between">
            <span class="text-zinc-500">Plan</span>
            <span class="font-semibold">{{
              $store.state.planDetails.planName
            }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Premium</span>
            <span class="font-semibold">{{
              amountPaymentChannelFormat($store.state.planDetails.premium)
            }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Payment Option</span>

            <span
              class="rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-600"
            >
              Full Payment
            </span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Member Name</span>
            <span class="font-semibold">Juan Dela Cruz</span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Card Number</span>
            <span class="font-semibold">IWC-A-00072</span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">School Name</span>
            <span class="font-semibold">ABC Elementary School</span>
          </div>

          <div class="flex justify-between">
            <span class="text-zinc-500">Coverage Period</span>
            <span class="font-semibold text-right">
              July 18, 2026 - July 17, 2027
            </span>
          </div>
        </div>
      </div>

      <!-- Amount -->
      <div class="rounded-2xl border border-orange-200 bg-orange-50 shadow-sm">
        <form class="p-6" @submit.prevent="submitCheckout()">
          <p class="text-sm text-zinc-500">Amount to Pay</p>

          <h2 class="mt-2 text-5xl font-bold text-orange-600">
            {{ amountPaymentChannelFormat($store.state.planDetails.amountPay) }}
          </h2>

          <p class="mt-3 text-sm text-zinc-500">
            One-time payment for your HMO plan renewal.
          </p>

          <div class="my-6 border-t border-orange-200"></div>

          <div class="space-y-3 text-sm text-zinc-700">
            <div class="flex items-center">
              <span class="mr-2 text-green-600">✔</span>
              Secure online payment
            </div>

            <div class="flex items-center">
              <span class="mr-2 text-green-600">✔</span>
              Instant payment confirmation
            </div>

            <div class="flex items-center">
              <span class="mr-2 text-green-600">✔</span>
              No recurring charges
            </div>
          </div>

          <button
            type="submit"
            :disabled="!agreed"
            :class="[
              'mt-8 w-full rounded-xl py-3 font-semibold transition',
              agreed
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'cursor-not-allowed bg-zinc-300 text-zinc-500',
            ]"
          >
            Proceed to Payment
          </button>
        </form>
      </div>
    </div>

    <!-- Terms & Conditions -->
    <div class="mt-8 rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div class="border-b border-zinc-200 p-6">
        <h3 class="text-lg font-semibold text-secondary">Terms & Conditions</h3>

        <p class="mt-2 text-sm text-zinc-500">
          Please review the following information before completing your
          payment.
        </p>
      </div>

      <div class="space-y-6 p-6">
        <!-- Important -->
        <div class="rounded-xl border border-orange-200 bg-orange-50 p-5">
          <h4 class="mb-4 font-semibold text-orange-600">
            Important Reminders
          </h4>

          <ul class="space-y-3 text-sm leading-6 text-zinc-700">
            <li>
              • Your payment will be processed through our secure payment
              gateway.
            </li>

            <li>
              • Your HMO renewal will only be activated after successful payment
              verification.
            </li>

            <li>
              • Please verify your membership details and payment information
              before proceeding.
            </li>

            <li>
              • Once successfully processed, payments are subject to the
              applicable refund and cancellation policies of the HMO provider.
            </li>

            <li>
              • A confirmation receipt will be sent after your payment has been
              completed.
            </li>
          </ul>
        </div>

        <!-- Agreement -->
        <div class="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
          <label class="flex cursor-pointer items-start gap-4">
            <input
              v-model="agreed"
              type="checkbox"
              class="mt-1 h-5 w-5 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
            />

            <span class="text-sm leading-7 text-zinc-700">
              I confirm that I have reviewed all payment details and agree to
              the
              <router-link
                to="/terms-and-conditions"
                class="font-semibold text-orange-600 hover:underline"
              >
                Terms and Conditions
              </router-link>
              and
              <router-link
                to="/privacy-policy"
                class="font-semibold text-orange-600 hover:underline"
              >
                Privacy Policy
              </router-link>
              governing this HMO renewal. I authorize the processing of this
              payment and acknowledge that my renewal is subject to successful
              payment verification and the applicable HMO policies.
            </span>
          </label>
        </div>

        <!-- Note -->
        <div class="rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
          <p class="text-sm leading-6 text-blue-800">
            <strong>Note:</strong> By clicking
            <strong>"Proceed to Payment"</strong>, you will be redirected to our
            secure payment gateway to complete your transaction.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reusableBase from "@/utils/composables/format";
import { reusablePaymentForm } from "@/utils/composables/payment_center";
import { ref, onMounted } from "vue";

export default {
  setup() {
    const { amountPaymentChannelFormat } = reusableBase();
    const { planDetails, submitCheckout } = reusablePaymentForm();
    const agreed = ref(false);
    onMounted(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
    return {
      agreed,
      planDetails,
      amountPaymentChannelFormat,
      submitCheckout,
    };
  },
};
</script>
